// src/app/modules/Auth/_redux/authRedux.js
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { put, takeLatest, call } from "redux-saga/effects";
import {
  getUserByToken,
  getUserOrganizations,
  switchOrganization,
  updateOrganization,
  getOrganizationLocations,
  addOrganizationLocation,
  updateOrganizationLocation,
  removeOrganizationLocation,
  setPrimaryLocation,
} from "./authCrud";

export const actionTypes = {
  // Auth
  Login: "[Login] Action",
  Logout: "[Logout] Action",
  Register: "[Register] Action",
  RESET_TOKEN: "[RESET] TOKEN",
  Error: "Set Error",

  // User
  UserRequested: "[Request User] Action",
  UserLoaded: "[Load User] Auth API",

  // Organizations
  OrgLoaded: "[Load Org] Action",
  OrgsLoaded: "[Load Orgs] Action",
  OrgUpdated: "[Update Org] Action",

  SwitchOrgRequested: "[Switch Org] Action",

  // Locations
  LocationsLoaded: "[Load Locations] Action",
  LocationAdded: "[Add Location] Action",
  LocationUpdated: "[Update Location] Action",
  LocationRemoved: "[Remove Location] Action",
  PrimaryLocationSet: "[Set Primary Location] Action",
};

const initialAuthState = {
  user: undefined,
  organization: undefined,       // active organization (full object)
  organizations: [],             // all user organizations
  locations: [],                 // locations for the active organization
  authToken: undefined,
  refreshToken: undefined,
  error: null,
};

export const reducer = persistReducer(
  {
    storage,
    key: "sety-v2-auth",
    whitelist: ["user", "authToken", "refreshToken", "organization", "organizations"],
    // NOTE: locations are NOT persisted — always fetched fresh to avoid stale geo data
  },
  (state = initialAuthState, action) => {
    switch (action.type) {

      // ── Auth ──────────────────────────────────────────────────────────────
      case actionTypes.Login: {
        const { authToken, refreshToken } = action.payload;
        return { ...state, authToken, refreshToken, user: undefined };
      }

      case actionTypes.Register: {
        const { authToken, refreshToken } = action.payload;
        return { ...state, authToken, refreshToken, user: undefined };
      }

      case actionTypes.Logout: {
        return initialAuthState;
      }

      case actionTypes.RESET_TOKEN: {
        const { authToken, refreshToken } = action.payload;
        return { ...state, authToken, refreshToken };
      }

      // ── User ─────────────────────────────────────────────────────────────
      case actionTypes.UserLoaded: {
        const { user } = action.payload;
        return { ...state, user };
      }

      // ── Organizations ─────────────────────────────────────────────────────
      case actionTypes.OrgLoaded: {
        const { organization } = action.payload;
        return { ...state, organization };
      }

      case actionTypes.OrgsLoaded: {
        const { organizations } = action.payload;
        // Keep current active org if still in the list, otherwise default to first
        const currentId = state.organization?.organization_id;
        const stillExists = organizations.find((o) => o.organization_id === currentId);
        const activeOrg = stillExists || organizations[0] || undefined;
        return { ...state, organizations, organization: activeOrg };
      }

      case actionTypes.OrgUpdated: {
        const { organization } = action.payload;
        // Update both the active org and the matching entry in organizations[]
        const organizations = state.organizations.map((o) =>
          o.organization_id === organization.organization_id ? organization : o
        );
        return { ...state, organization, organizations };
      }

      // ── Locations ─────────────────────────────────────────────────────────
      case actionTypes.LocationsLoaded: {
        return { ...state, locations: action.payload.locations };
      }

      case actionTypes.LocationAdded: {
        return {
          ...state,
          locations: [...state.locations, action.payload.location],
          // If it's primary, sync the active org's address
          organization: action.payload.location.is_primary
            ? { ...state.organization, address: action.payload.address }
            : state.organization,
        };
      }

      case actionTypes.LocationUpdated: {
        const updated = action.payload.location;
        const locations = state.locations.map((l) =>
          l.location_id === updated.location_id ? updated : l
        );
        // If primary changed, sync address on org
        const org = updated.is_primary
          ? { ...state.organization, address: action.payload.address }
          : state.organization;
        return { ...state, locations, organization: org };
      }

      case actionTypes.LocationRemoved: {
        return {
          ...state,
          locations: state.locations.filter(
            (l) => l.location_id !== action.payload.locationId
          ),
        };
      }

      case actionTypes.PrimaryLocationSet: {
        const { locationId, address } = action.payload;
        const locations = state.locations.map((l) => ({
          ...l,
          is_primary: l.location_id === locationId,
        }));
        return {
          ...state,
          locations,
          organization: { ...state.organization, address },
        };
      }

      // ── Error ─────────────────────────────────────────────────────────────
      case actionTypes.Error: {
        return { ...state, error: action.payload.error };
      }

      default:
        return state;
    }
  }
);

// ── Action creators ───────────────────────────────────────────────────────────

export const actions = {
  // Auth
  login: (authToken, refreshToken) => ({
    type: actionTypes.Login,
    payload: { authToken, refreshToken },
  }),
  register: (authToken, refreshToken) => ({
    type: actionTypes.Register,
    payload: { authToken, refreshToken },
  }),
  logout: () => ({ type: actionTypes.Logout }),
  fulfillToken: (authToken, refreshToken) => ({
    type: actionTypes.RESET_TOKEN,
    payload: { authToken, refreshToken },
  }),

  // User
  requestUser: () => ({ type: actionTypes.UserRequested }),
  fulfillUser: (user) => ({ type: actionTypes.UserLoaded, payload: { user } }),

  // Organizations
  fulfillOrg: (organization) => ({
    type: actionTypes.OrgLoaded,
    payload: { organization },
  }),
  fulfillOrgs: (organizations) => ({
    type: actionTypes.OrgsLoaded,
    payload: { organizations },
  }),
  orgUpdated: (organization) => ({
    type: actionTypes.OrgUpdated,
    payload: { organization },
  }),

  requestSwitchOrg: (organizationId) => ({ 
    type: actionTypes.SwitchOrgRequested, 
    payload: { organizationId } 
  }),

  // Locations — dispatched by components after API calls resolve
  locationsLoaded: (locations) => ({
    type: actionTypes.LocationsLoaded,
    payload: { locations },
  }),
  locationAdded: (location, address) => ({
    type: actionTypes.LocationAdded,
    payload: { location, address },
  }),
  locationUpdated: (location, address) => ({
    type: actionTypes.LocationUpdated,
    payload: { location, address },
  }),
  locationRemoved: (locationId) => ({
    type: actionTypes.LocationRemoved,
    payload: { locationId },
  }),
  primaryLocationSet: (locationId, address) => ({
    type: actionTypes.PrimaryLocationSet,
    payload: { locationId, address },
  }),

  // Error
  setError: (error) => ({ type: actionTypes.Error, payload: { error } }),
};

// ── Sagas ─────────────────────────────────────────────────────────────────────

export function* saga() {
  yield takeLatest(actionTypes.Login, function* loginSaga() {
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.Register, function* registerSaga() {
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.UserRequested, function* userRequested() {
    try {
      // Load organizations (includes subscription status, address, etc.)
      const { data: orgsData } = yield call(getUserOrganizations);

      if (orgsData?.organizations) {
        yield put(actions.fulfillOrgs(orgsData.organizations));
      }

      // Optionally load the user profile too
      // const { data: userData } = yield call(getUserByToken);
      // if (userData?.user) yield put(actions.fulfillUser(userData.user));

    } catch (error) {
      console.error("Error loading user data:", error);
      yield put(actions.setError(error.message));
    }
  });


    yield takeLatest(actionTypes.SwitchOrgRequested, function* switchOrgSaga(action) {
    try {
      yield put(actions.startCall());
      
      const { organizationId } = action.payload;
      const { data: response } = yield call(switchOrganization, organizationId);
      // alert(JSON.stringify(response));
      if (response && response.organization) {
        yield put(actions.fulfillOrg(response.organization));
        
        // Toast.show({ 
        //   type: "success", 
        //   text1: 'Organization Switched', 
        //   text2: `Now viewing ${response.organization.name}` 
        // });

        // functions.showError(`Successfully switched to ${response.organization.name}`, 'success');

      }
    } catch (error) {
      console.error('Error switching organization:', error);
      const message = error.response?.data?.message || error.message || "Failed to switch organization";
      
      // Toast.show({ 
      //   type: "error", 
      //   text1: 'Error', 
      //   text2: message 
      // });
      
      yield put(actions.setError(message));
    }
  });


}